/**
 * @fileoverview Renders a map with drawing manager connected so it is possible to draw
 * Contains and operates all logic for draw, save, delete, undo actions
 */
import React, { useEffect, useState, useRef, useCallback } from 'react'
import { connect } from 'react-redux'
import DrawMapView from '../component/DrawMapView'
import DrawingManagerMap from '../../GMap/DrawingManagerMap'
import DrawMapControls from '../components/DrawMapControls/DrawMapControls'
import { drawingManagerOptions } from '../settings'
import ShapeSettingsModal from '../../ShapeSettingsModal/container/ShapeSettingsModal'
import {
  getOverlayBoundary,
  getAreaData,
  generateModalExtraStyles,
  getFilters,
  getDensities,
} from '../util'
import { generateRandomColor } from '../../../Utils/Misc'
import { debounce } from '../../../Utils/Functions'
import { showReduxModal } from '../../../Reducers/UI/actions'
import { getNumberOfHouseholdsAndDensityForBoundaries } from '../../../Utils/Map'
import {
  getPolygonAreaInKm2,
  getTimeNeededForFlyersDistribution,
} from '../../../shared/libraries/geo'
import { timeConvert } from '../../../shared/libraries/utils/date'

const actionTypes = {
  deleteOverlay: 'deleteOverlay',
  createOverlay: 'createOverlay',
  editOverlay: 'editOverlay',
}

const DrawMap = ({
  onSave,
  initialOverlays,
  onDelete,
  hideMapControls,
  countryCode,
  visibleOverlaysMap,
  onOverlayClick,
  shouldPickWeek = true,
  onMapInit,
  loading,
  mapOptions,
  mapControls = ['DRAW'],
  showReduxModal,
  onDrawingChange = () => {},
  filter,
  onFilterLoaded,
}) => {
  const [map, setMap] = useState(null)
  const [modalVisible, setModalVisible] = useState(false)
  const [isDrawing, setIsDrawing] = useState(false)
  const [positionInsideParent, setPositionInsideParent] = useState({})
  const [mapHistory, setMapHistory] = useState([])
  const [infoRows, setInfoRows] = useState([])
  const [overlays, setOverlays] = useState([])
  const [selectedOverlay, setSelectedOverlay] = useState(null)
  const [drawingManager, setDrawingManager] = useState(null)
  const [saving, setSaving] = useState(false)
  const initialOverlaysRef = useRef()
  const [densityFilters, setDensityFilters] = useState(null)
  /**
   * Effect that clears up after unmount
   */
  useEffect(
    () => () => {
      setOverlays([])
      setSelectedOverlay(null)
      setDrawingManager(null)
    },
    [],
  )
  /**
   * Keep track of the latest initial overlays for
   * onSaveHandler that was created on gmaps overlay render
   * and dont have access to the newest props since he's not rerendered
   */
  useEffect(() => {
    initialOverlaysRef.current = initialOverlays
  }, [initialOverlays])

  /**
   * Effect that hides/shows overlays based on visibleOverlaysMap
   */
  useEffect(() => {
    if (!visibleOverlaysMap) return
    setOverlays(overlays => {
      const densityRange = (densityFilters &&
        filter &&
        densityFilters[filter.toLowerCase()]) || [0, Infinity]

      overlays.forEach(overlay => {
        let hideByFilter = false
        const hideByVisibleOverlay =
          visibleOverlaysMap && !visibleOverlaysMap[overlay.customId]

        if (filter && filter !== 'ALL' && densityFilters) {
          let [density] =
            'areaData' in overlay
              ? overlay.areaData
                  .find(item => item.name === 'Density')
                  .value.split(' ')
              : [0]
          density = isNaN(+density) ? 0 : +density

          hideByFilter = !(
            density >= densityRange[0] && density < densityRange[1]
          )
        }

        if (hideByVisibleOverlay || hideByFilter) {
          overlay.setMap(null)
        } else {
          if (!overlay.map) {
            overlay.setMap(map)
          }
        }
      })
      return overlays
    })
  }, [visibleOverlaysMap, overlays, densityFilters, filter])

  useEffect(() => {
    if (overlays && overlays.length) {
      setAllAreaData(overlays)
    }
  }, [overlays])

  const saveOverlay = overlay =>
    setOverlays(overlays => {
      const overlaysClone = overlays.filter(existingOverlay => {
        if (overlay.customId == existingOverlay.customId) {
          existingOverlay.setMap(null)
        }
        return overlay.customId != existingOverlay.customId
      })
      return [...overlaysClone, overlay]
    })

  /**
   * Receives overlay and saves it to the overlays array,
   * adds a listener to it,
   * sets it as selected overlay
   * and clears other selections
   *
   * @param {MVCObject} overlay GMAP Overlay
   */
  const onOverlayComplete = async ({ overlay }) => {
    stopDrawing(overlay)
    clearSelection()
    const boundary = getOverlayBoundary(overlay)
    if (boundary.length < 3) {
      overlay.setMap(null)
      alert('Overlay should have at least 3 points')
      return
    }
    const randomColor = generateRandomColor()
    overlay.set('fillColor', randomColor)
    overlay.set('strokeColor', randomColor)
    overlay.set('zIndex', 25)
    onSaveHandler({
      overlay,
      callback: processedOverlay => {
        initOverlay(processedOverlay)
        setSelection(processedOverlay)
      },
    })
  }

  /**
   * Receives overlay and sets it as selected overlay after clearing other selections
   * Makes settings modal visible
   *
   * @param {MVCObject} overlay GMAP Overlay
   */
  const setSelection = useCallback(overlay => {
    if (overlay.disabled) return
    clearSelection()
    setSelectedOverlay(() => {
      if (overlay.areaData) {
        setInfoRows(overlay.areaData)
      } else {
        setAreaData(overlay)
      }
      setModalVisible(true)
      overlay.set('strokeWeight', 6)
      if (overlay.editingBlocked) return overlay
      overlay.setEditable(true)
      overlay.setDraggable(true)
      return overlay
    })
  }, [])

  /**
   * Removes selected status from overlay and hides settings modal
   */
  const clearSelection = () =>
    setSelectedOverlay(selectedOverlay => {
      if (selectedOverlay) {
        selectedOverlay.set('strokeWeight', 2)
        selectedOverlay.setEditable(false)
        selectedOverlay.setDraggable(false)
      }
      setModalVisible(false)
      setInfoRows([])
      return null
    })

  /**
   * Wrapper for deleteOverlay that saves action to the map history so it can be reverted
   * @param {MVCObject} overlay
   */
  const deleteOverlayConnected = overlay => {
    deleteOverlay(overlay)

    if (
      overlays.filter(({ customId }) => customId == overlay.customId).length
    ) {
      saveActionToMapHistory({
        type: actionTypes.deleteOverlay,
        payload: {
          overlay,
        },
      })
    }
  }
  /**
   * Deletes overlay and calls related callback provided in props
   * Hides modal and clears selection
   *
   * @param {MVCObject} overlay
   */
  const deleteOverlay = async overlay => {
    if (overlay) {
      setModalVisible(false)
      await onDelete([overlay], false)
      overlay.setMap(null)
      clearSelection()
      setOverlays(overlays =>
        overlays.filter(({ customId }) => customId !== overlay.customId),
      )
    }
  }

  /**
   * Saves history action to the map history so it can be reverted
   * @param {object} action
   * @param {string} action.type Type of the action - is used to choose reverting action
   * @param {object} action.payload Data that needed for reverting
   */
  const saveActionToMapHistory = action =>
    setMapHistory([...mapHistory, action])

  /**
   * Calls revert on last action that was saved to the map history and pops it, then saves new history
   * Stops drawing
   */
  const undoHandler = async () => {
    stopDrawing()
    setMapHistory(async mapHistory => {
      if (mapHistory && mapHistory.length > 0) {
        const mapHistoryClone = [...mapHistory]
        const lastAction = mapHistoryClone.pop()
        await revertAction(lastAction)
        return mapHistoryClone
      }
    })
  }

  /**
   * Handles reverting of the received action using received type to
   * choose correct reverting action and payload to call with a revert
   *
   * @param {object} action
   * @param {string} action.type Type of the action - is used to choose reverting action
   * @param {object} action.payload Data that needed for reverting
   */
  const revertAction = async action => {
    if (action) {
      const { type, payload } = action
      if (type === actionTypes.createOverlay) {
        await deleteOverlay(payload.overlay)
      }
      if (type === actionTypes.deleteOverlay) {
        const { overlay } = payload

        overlay.customId = null
        overlay.setMap(map)
        await onSaveHandler({ overlay, saveToHistory: false })
      }
      if (type === actionTypes.editOverlay) {
        const { id, paths, color, mapPolygonProps } = payload.prevOverlay

        setOverlays(overlays =>
          overlays.filter(existingOverlay => {
            if (existingOverlay.customId == id) {
              existingOverlay.setMap(null)
            }
            return existingOverlay.customId !== id
          }),
        )

        const overlay = new google.maps.Polygon({
          customId: id,
          key: id,
          clickable: true,
          map: map,
          paths,
          strokeColor: color,
          fillColor: color,
          canBeDeleted: true,
          ...mapPolygonProps,
        })
        initOverlay(overlay)

        await onSaveHandler({
          overlay,
          callback: setSelection,
          saveToHistory: false,
        })
      }
    }
  }

  /**
   * Calls props.onDelete action on all saved overlays
   * Clears selection
   * Hides modal
   */
  const removeAll = async () => {
    if (overlays.length > 0) {
      clearSelection()
      await onDelete(overlays)
      setModalVisible(false)
    }
  }

  /**
   * Toggles drawing mode
   */
  const toggleDrawing = () => {
    if (drawingManager) {
      if (drawingManager.getDrawingMode()) {
        stopDrawing()
      } else {
        startDrawing()
      }
    }
    setModalVisible(false)
  }

  /**
   * Turns off drawing mode
   */
  const stopDrawing = useCallback(
    overlay =>
      setDrawingManager(drawingManager => {
        drawingManager.setDrawingMode(null)
        setIsDrawing(false)
        onDrawingChange(false)
        return drawingManager
      }),
    [],
  )

  /**
   * Turns on drawing mode
   */
  const startDrawing = () =>
    setDrawingManager(drawingManager => {
      drawingManager.setDrawingMode(google.maps.drawing.OverlayType.POLYGON)
      setIsDrawing(true)
      onDrawingChange(true)
      return drawingManager
    })

  /**
   * Gets data from selected overlay and calls props.onSave with it
   * Then updates selected overlay with received ID so overlay can be correctly tracked on the map
   */
  const onSaveHandler = debounce(
    async ({
      overlay,
      callback = () => {},
      saveToHistory = true,
      payload = {},
    }) => {
      try {
        setSaving(true)
        const isEdit = !!overlay.customId
        if (saveToHistory && isEdit) {
          saveEditActionToHistory(overlay.customId)
        }
        const { customId, fillColor: color } = overlay
        const boundary = getOverlayBoundary(overlay)
        const result = await onSave({
          id: customId,
          color,
          boundary,
          date: payload.date,
          label: payload.label,
          overlay,
        })
        overlay.set('customId', result.id)
        overlay.set('key', result.id)
        callback(overlay)
        setAreaData(overlay)
        if (saveToHistory && !isEdit) {
          saveCreateActionToHistory(overlay)
        }
      } catch (error) {
        console.error(error)
        showReduxModal({
          title: `Overlay saving failed`,
          onConfirm: () => onSaveHandler({ overlay, callback, saveToHistory }),
          onCancel: () => {
            clearSelection()
            if (overlay.customId) {
              undoHandler()
            } else {
              overlay.setMap(null)
            }
          },
          children: (
            <div>
              <p>Reason: {JSON.stringify(error.message)}</p>
              {!!overlay.customId ? (
                <p>Changes will be reverted</p>
              ) : (
                <p>Cancel will remove failed overlay from the map</p>
              )}
            </div>
          ),
          confirmLabel: 'Retry',
          cancelLabel: 'Cancel',
        })
      } finally {
        setSaving(false)
      }
    },
    600,
  )

  /**
   * Save create action overlay so it can de deleted on undo
   * @param {object} overlay Overlay
   */
  const saveCreateActionToHistory = overlay =>
    saveActionToMapHistory({
      type: actionTypes.createOverlay,
      payload: {
        overlay,
      },
    })

  /**
   * Save prev state of the edited overlay so it can de undone
   * @param {string} id Overlay's id
   */
  const saveEditActionToHistory = id =>
    saveActionToMapHistory({
      type: actionTypes.editOverlay,
      payload: {
        prevOverlay: initialOverlaysRef.current.find(
          initialOverlay => initialOverlay.id === id,
        ),
      },
    })

  /**
   * Saves cursor position to the components state
   * Cursor position used for displaying settings modal next to the user's cursor
   * @param {object} position
   */
  const handleSetPositionInsideParent = position =>
    setPositionInsideParent(generateModalExtraStyles(position))

  /**
   * Initiates overlay when it is rendered first time from initialOverlays data in the Map component
   * Adds listener and saves to the overlays array
   * @param {MVCObject} overlay
   */
  const initOverlay = overlay => {
    if (
      overlay.customToggled === true &&
      typeof onOverlayClick === 'function'
    ) {
      onOverlayClick(overlay)
    }
    google.maps.event.addListener(overlay, 'click', e => {
      setSelection(overlay)
      if (typeof onOverlayClick === 'function') {
        onOverlayClick(overlay)
      }
    })
    overlay.getPaths().forEach((path, index) => {
      google.maps.event.addListener(
        path,
        'insert_at',
        () => onSaveHandler({ overlay }), // New point
      )
      google.maps.event.addListener(
        path,
        'remove_at',
        () => onSaveHandler({ overlay }), // Point was removed
      )
      google.maps.event.addListener(
        path,
        'set_at',
        () => onSaveHandler({ overlay }), // Point was moved, dragend captured here as well
      )
    })
    saveOverlay(overlay)
  }
  /**
   * Get area's data
   */
  const setAreaData = async overlay => {
    if (!overlay || !overlay.getPath) return
    setInfoRows([
      {
        name: 'Addresses',
        value: 'Loading',
      },
      {
        name: 'Density',
        value: 'Loading',
      },
    ])
    const areaData = await getAreaData(overlay, countryCode)
    setInfoRows(areaData)
    overlay.set('areaData', areaData)
  }

  const setAllAreaData = async overlays => {
    if (overlays.every(overlay => overlay.areaData)) return
    setDensityFilters(null)
    onFilterLoaded && onFilterLoaded(false)
    const boundaries = overlays.map(overlay => getOverlayBoundary(overlay))
    const householdsAndDensities = await getNumberOfHouseholdsAndDensityForBoundaries(
      {
        boundaries,
        countryCode,
      },
    )

    const hashMapHouseholdsAndDensity = overlays.reduce(
      (acc, { customId }, index) => {
        const { density, householdsAmount } = householdsAndDensities[index]
        const polygonAreaSize = getPolygonAreaInKm2(boundaries[index])
        const approximateDistTime = getTimeNeededForFlyersDistribution({
          areaSize: polygonAreaSize,
          householdsAmount: householdsAmount,
        })
        acc[customId] = [
          {
            name: 'Addresses',
            value: householdsAmount,
          },
          {
            name: 'Density',
            value: `${density} HH/km2`,
          },
          {
            name: 'Area Size',
            value: `${polygonAreaSize.toFixed(3)} km2`,
          },
          {
            name: 'Approximate dist time',
            value: timeConvert(approximateDistTime * 60),
          },
        ]
        return acc
      },
      {},
    )
    const newOverlays = overlays.map(overlay => {
      const areaData = hashMapHouseholdsAndDensity[overlay.customId]
      overlay.set('areaData', areaData)
      return overlay
    })

    if (newOverlays.length) {
      const densities = getDensities(newOverlays)
      const filters = getFilters(densities)
      setDensityFilters(filters)
      onFilterLoaded && onFilterLoaded(true)
    }
    setOverlays(newOverlays)
  }

  return (
    <DrawMapView
      loading={loading}
      setCursorPosition={handleSetPositionInsideParent}
      hideMapControls={hideMapControls}
      controls={
        <DrawMapControls
          isDrawing={isDrawing}
          toggleDrawing={toggleDrawing}
          undoHandler={undoHandler}
          removeAll={removeAll}
          mapControls={mapControls}
          shouldShowUndo={Boolean(mapHistory.length) && !isDrawing}
          saving={saving}
        />
      }
    >
      <DrawingManagerMap
        setDrawingManager={drawingManager =>
          drawingManager && setDrawingManager(drawingManager)
        }
        onMapInit={map => {
          setMap(map)
          if (onMapInit) {
            onMapInit(map, setModalVisible)
          }
        }}
        onOverlayComplete={onOverlayComplete}
        initOverlay={initOverlay}
        mapOptions={mapOptions}
        drawingManagerOptions={drawingManagerOptions}
        initialOverlays={initialOverlays}
        overlays={overlays}
      />
      <ShapeSettingsModal
        visible={modalVisible}
        onDelete={() => deleteOverlayConnected(selectedOverlay)}
        onUpdate={(overlay, payload) => onSaveHandler({ overlay, payload })}
        onClose={() => setModalVisible(false)}
        positionInsideParent={positionInsideParent}
        selectedOverlay={selectedOverlay}
        shouldPickWeek={shouldPickWeek}
        countryCode={countryCode}
        infoRows={infoRows}
        saving={saving}
        viewMode={hideMapControls}
      />
    </DrawMapView>
  )
}

const mapDispatchToProps = {
  showReduxModal,
}

export default connect(
  null,
  mapDispatchToProps,
)(DrawMap)



// WEBPACK FOOTER //
// ./src/Components/DrawMap/container/DrawMap.js